import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { createGroupWidget } from 'actions/group';
import { useSelectedWidgetSelector, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';

import { EditorContext } from 'components/Editor';
import { Property } from '../../components/properties/';
import { LiveElementCreationProperty } from '../../components/liveElements';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';

import { WidgetRegistry } from 'components/Editor/widgets/registry';

import { BUTTON, FORM, HTML, IFRAME, IMAGE, LINE, SHAPE, TEXT, VIDEO, GROUP, RECTANGLE, MENU_CONTAINER, HOTSPOT, CONTENT_CARD } from 'constants/widgetTypes';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';

import styles from './toolbar.module.scss';

const ALLOWED_LIVE_ELEMENTS = [
  TEXT, BUTTON, SHAPE, LINE, FORM, IMAGE, VIDEO, HTML, IFRAME, RECTANGLE
];

const ALLOWED_GROUP = [
  TEXT, BUTTON, SHAPE, LINE, IMAGE, VIDEO, HTML, IFRAME, GROUP, RECTANGLE
]

function WidgetToolBar({ rect, showActions }) {
  const dispatch = useDispatch();
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const [dragStyles, setDragStyles] = useState({
    left: -8,
    bottom: 0
  });

  const widget = useSelector(state => {
    const selected = _.get(state, `${editorSession}.present.selected`);
    if (!selected || selected.length > 1) {
      return null;
    }

    const id = selected[0];
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find(item => item.uid === id);
  });

  useEffect(() => {
    var left = -8;
    var bottom = 0;

    if (rect.left < 30) {
      left = -rect.left + 30;
      bottom = -25;
    }

    if (rect.top < 0) {
      bottom = rect.top - 20;
    }

    setDragStyles({
      left: left,
      bottom: bottom
    });
  }, [rect]);

  if (!widget) {
    return false;
  }

  return (
    <div className={cx('moveable-widget-toolbar', styles.toolbar)}>
      {showActions && 
        <div className={cx('moveable-widget-toolbar', styles.actions)}>
          {WidgetRegistry.actions(editorSession, widget.type, breakpoint, widget)}
        </div>
      }
      { widget.type !== MENU_CONTAINER &&
        <div className={cx('moveable-drag-target', styles.dragTarget)} style={dragStyles} >
          <FontAwesomeIcon icon={faExpandArrowsAlt} />
        </div>
      }
      <TruncatedTextWithTooltip className={cx(styles.title)} content={widget.name} maxWidth={150} placement='top'>
        {widget.name}
      </TruncatedTextWithTooltip>
    </div>
  );
}

const GroupToolBar = ({ elements }) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const entityType = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.entityType`));

  const selectedWidgets = useSelectedWidgetSelector(editorSession);
  var widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`) || []);

  const handleLiveElement = (e) => {
    setView('liveElement');
  }

  const canCreateLiveElement = React.useMemo(() => {
    var allowed = entityType !== 'LIVE_ELEMENT';

    selectedWidgets.forEach(widget => {
      allowed = allowed && _.includes(ALLOWED_LIVE_ELEMENTS, widget.type) 
        && !(widgets.some(w => w.parent === widget.uid && w.type === HOTSPOT))
        && !(widgets.some(w => w.uid === widget.parent && w.type === HOTSPOT));
    });

    var parents = selectedWidgets.map(el => el.parent);
    parents = _.uniq(parents);

    allowed = allowed && parents.some(parent => {
      var widget = widgets.find(w => w.uid === parent);
      return ![GROUP, CONTENT_CARD].includes(widget.type);;
    });

    return allowed && _.size(parents) == 1;
    
  }, [selectedWidgets, widgets]);

  const handleGroup = () => {
    dispatch(createGroupWidget(editorSession, selectedWidgets));
  }

  const canGroupWidgets = React.useMemo(() => {
    var allowed = true;

    var parents = selectedWidgets.map(el => el.parent).filter(el => el !== null);
    parents = _.uniq(parents);

    if (_.size(parents) === 1) {
      var parent = widgets.find(w => w.uid === parents[0]);
      allowed = ![GROUP, CONTENT_CARD].includes(parent.type);
    }

    selectedWidgets.forEach(widget => {
      allowed = allowed && _.includes(ALLOWED_GROUP, widget.type) 
        && !(widgets.some(w => w.parent === widget.uid && w.type === HOTSPOT));
    });

    return allowed && _.size(parents) === 1;
  }, [selectedWidgets, widgets]);

  if (!canCreateLiveElement && !canGroupWidgets) {
    return false;
  }

  return (
    <div className={cx('moveable-widget-toolbar', styles.toolbar)}>
      <div className={styles.actions}>
        {canCreateLiveElement &&
          <Property.Action className={styles.textButton} onClick={handleLiveElement}>Make Live Element</Property.Action>
        }
        {canGroupWidgets &&
          <Property.Action className={styles.textButton} onClick={handleGroup}>Group</Property.Action>
        }
        <Property.Divider />
        <Property.Action className={styles.iconButton} tooltip='Help' onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
          <i className={'cleverstory-help'} />
        </Property.Action>
      </div>
      {view === 'liveElement' &&
        <LiveElementCreationProperty elements={elements} onCancel={() => setView(null)} />
      }
    </div>
  )
}

export const ToolBarViewable = {
  name: "toolBarViewable",
  props: {},
  events: {},
  render(moveable, renderer) {
    const elements = moveable.props.targets;
    const isDragging = moveable.props.isDragging;
    const rect = moveable.getRect();

    if (_.size(elements) > 1) {
      return false;
    }

    return (
      <WidgetToolBar key={"widget-toolbar"} rect={rect} showActions={!isDragging} />
    );
  }
}

export const GroupToolBarViewable = {
  name: "groupToolBarViewable",
  props: {},
  events: {},
  render(moveable, renderer) {
    const elements = moveable.props.targets

    if (_.size(elements) == 1) {
      return false;
    }

    return (
      <GroupToolBar key={"group-widget-toolbar"} elements={elements} />
    );
  }
}